import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Self,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, AsyncValidator, ControlValueAccessor, NgControl } from '@angular/forms';
import { AccountingPeriod } from '@assetaccountant/api/models/accounting-period';
import { CalendarV2Api } from '@assetaccountant/api/services/calendar-v2.api';
import { toUniversalDateString } from '@assetaccountant/core';
import { FinancialYear } from '@assetaccountant/core/pipes/aa-fin-year.pipe';
import { compareProximityToDate } from '@assetaccountant/core/utils/date-utils';
import { isNotNull } from '@assetaccountant/core/utils/filter-nulls';
import { firstWithContext } from '@assetaccountant/core/utils/first-with-context';
import { emptyValue } from '@assetaccountant/core/utils/typed-forms';
import moment from 'moment';
import { BehaviorSubject, combineLatest, EMPTY, NEVER, Observable } from 'rxjs';
import { catchError, delay, map, shareReplay, switchMap, tap } from 'rxjs/operators';

enum GetMorePeriods {
  GetEarlierPeriods = 'GetEarlierPeriods',
  GetLaterPeriods = 'GetLaterPeriods',
}

@Component({
  selector: 'app-period-selector',
  templateUrl: './period-selector.component.html',
  styleUrls: ['./period-selector.component.scss'],
  standalone: false,
})
export class PeriodSelectorComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, AsyncValidator
{
  @Input() startDate: string;
  @Input() useEndDate = false;
  @Input() allowEmpty = false;
  @Input() includeStartDate = false;

  private _minDate$ = new BehaviorSubject<string | null>(null);
  @Input() set minDate(value: string | undefined | null) {
    this._minDate$.next(value ? toUniversalDateString(value) : null);
  }
  get minDate(): string | null {
    return this._minDate$.value;
  }
  @Input() registerId?: number;
  @Input() regimeId?: number;

  @Input() yearsBefore?: number;

  @Input() flexible = false;

  readonly GetMorePeriods = GetMorePeriods;

  private earliestPeriod?: AccountingPeriod;
  private latestPeriod?: AccountingPeriod;

  get hasEarlierPeriodsAvailable(): boolean {
    if (!this.earliestPeriod) return false;
    if (this.minDate && this.earliestPeriod.startDate <= this.minDate) return false;
    return true;
  }

  value: string = emptyValue<string>();

  usePeriodDate$ = new BehaviorSubject(true);

  isDisabled = false;
  isLoading = true;

  private calendarDates$ = new BehaviorSubject<
    Observable<Array<{ fy: FinancialYear | null; periods: string[] }>>
  >(NEVER);

  private datesWithCatch$ = this.calendarDates$.pipe(
    switchMap(x$ => x$.pipe(catchError(_ => EMPTY))),
  );

  periods$ = this.datesWithCatch$.pipe(map(fys => fys.flatMap(fy => fy.periods).filter(isNotNull)));

  filteredCalendarDates$ = combineLatest([this.datesWithCatch$, this._minDate$]).pipe(
    map(([fys, minDate]) => {
      if (!minDate) {
        return fys;
      }

      return fys
        .map(fy => {
          if (fy.periods[fy.periods.length - 1] < minDate) {
            return null;
          }
          if (minDate <= fy.periods[0]) {
            return fy;
          }
          return {
            fy: fy.fy,
            periods: fy.periods.filter(p => minDate <= p),
          };
        })
        .filter(isNotNull);
    }),
  );

  private _onChange?: (val: string | null) => void;
  private _onTouched?: () => void;

  private _subscriptions = [
    this.usePeriodDate$.pipe(delay(1)).subscribe(usePeriodDate => {
      if (usePeriodDate && this.value) {
        if (this.allowEmpty && this.value === this.minDate) {
          this.value = '';
          this.control.control?.updateValueAndValidity();
          this.onChange();
        } else {
          this.periods$.subscribe(periods => {
            this.value = periods.slice().sort(compareProximityToDate(this.value))[0];
            this.control.control?.updateValueAndValidity();
            this.onChange();
          });
        }
      } else {
        this.control.control?.updateValueAndValidity();
      }
    }),
  ];

  constructor(
    @Self() public control: NgControl,
    private calendarApi: CalendarV2Api,
    private cd: ChangeDetectorRef,
  ) {
    this.control.valueAccessor = this;
  }

  ngOnInit() {
    if (this.control.control) {
      this.control.control.setAsyncValidators(c => this.validate(c));
      this.control.control.updateValueAndValidity();
    }

    this.ngOnChanges({});
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    const minDate =
      changes.minDate?.currentValue && toUniversalDateString(changes.minDate?.currentValue);

    if (minDate && this.latestPeriod?.startDate) {
      if (moment.utc(minDate).isAfter(moment.utc(this.latestPeriod?.startDate).add(-2, 'years'))) {
        const endDate = toUniversalDateString(moment.utc(minDate).add(10, 'years'));

        this.updateCalendarDates(null, endDate);
      }
    }

    if (Object.keys(changes).length === 1 && 'minDate' in changes) {
      return;
    }

    const startDate = toUniversalDateString(this.startDate);

    if (startDate) {
      this.minDate = startDate;
    }

    this.isLoading = true;

    this.updateCalendarDates(startDate, null, 'regimeId' in changes);
  }

  onChange() {
    if (this.value === GetMorePeriods.GetEarlierPeriods) {
      this.onGetMorePeriods(GetMorePeriods.GetEarlierPeriods);
      this.value =
        (!this.useEndDate ? this.earliestPeriod?.startDate : this.earliestPeriod?.endDate) || '';
    } else if (this.value === GetMorePeriods.GetLaterPeriods) {
      this.onGetMorePeriods(GetMorePeriods.GetLaterPeriods);
      this.value =
        (!this.useEndDate ? this.latestPeriod?.startDate : this.latestPeriod?.endDate) || '';
    }

    this._onChange?.(this.value);
  }

  onGetMorePeriods(direction: GetMorePeriods) {
    this.isLoading = true;

    const startDate =
      direction === GetMorePeriods.GetEarlierPeriods
        ? toUniversalDateString(moment.utc(this.earliestPeriod?.startDate).add(-10, 'years'))
        : this.startDate;

    const endDate =
      direction === GetMorePeriods.GetLaterPeriods
        ? toUniversalDateString(moment.utc(this.latestPeriod?.endDate).add(10, 'years'))
        : null;

    this.updateCalendarDates(startDate, endDate);
  }

  onTouched() {
    this._onTouched?.();
  }

  validate(_control: AbstractControl) {
    // This marks the control as pending while calendarPeriods are loading
    // If not using a period date mark make input required
    // Returns null to indicate valid
    return this.periods$.pipe(
      firstWithContext(),
      delay(1),
      map(() => {
        if (!this.usePeriodDate$.value) {
          if (!this.value) {
            return { required: true };
          }
        }
        return null;
      }),
    );
  }

  writeValue(obj: any) {
    this.value = toUniversalDateString(obj) || emptyValue<string>();
  }
  registerOnChange(fn: any) {
    this._onChange = fn;
  }
  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  private updateCalendarDates(
    startDate: string | null,
    endDate: string | null,
    regimeIdChanged: boolean = false,
  ) {
    this.calendarDates$.next(
      this.calendarApi
        .getPeriods({
          registerId: this.registerId || null,
          regimeId: this.regimeId || null,
          startDate,
          endDate,
        })
        .pipe(
          map(({ periods }) => {
            this.earliestPeriod = periods[0];
            this.latestPeriod = periods[periods.length - 1];

            const results: Array<{ fy: FinancialYear | null; periods: string[] }> = [];

            let currentFy = '';
            if (this.includeStartDate && startDate) {
              currentFy = periods[0].fyStartDate;

              results.push({
                fy: { startDate: periods[0].fyStartDate, endDate: periods[0].fyEndDate },
                periods: [startDate],
              });
            }

            for (const period of periods) {
              if (currentFy !== period.fyStartDate) {
                currentFy = period.fyStartDate;

                results.push({
                  fy: { startDate: period.fyStartDate, endDate: period.fyEndDate },
                  periods: [],
                });
              }

              const periodDate = toUniversalDateString(
                this.useEndDate ? period.endDate : period.startDate,
                { throwOnFailure: true },
              );
              if (!startDate || startDate < periodDate) {
                results[results.length - 1].periods.push(periodDate);
              }
            }

            return results;
          }),
          tap(results => {
            this.isLoading = false;
            if (regimeIdChanged && this.value) {
              const allPeriods = results.flatMap(x => x.periods);

              if (!allPeriods.includes(this.value)) {
                this.value = allPeriods.sort(
                  (a, b) =>
                    Math.abs(moment.utc(a).diff(this.value, 'days')) -
                    Math.abs(moment.utc(b).diff(this.value, 'days')),
                )[0];
              }
            }
            this.cd.detectChanges();
          }),
          shareReplay(1),
        ),
    );
  }
}
